export interface Mailbox {
  id: string;
  name: string;
  role: string | null;
  parentId: string | null;
  sortOrder?: number;
  totalEmails?: number;
  unreadEmails?: number;
}

export interface EmailAddress {
  name: string | null;
  email: string;
}

export interface MessageSummary {
  id: string;
  threadId: string;
  mailboxIds: Record<string, boolean>;
  keywords: Record<string, boolean>;
  from: EmailAddress[] | null;
  to: EmailAddress[] | null;
  subject: string | null;
  receivedAt: string;
  preview?: string | null;
  hasAttachment?: boolean;
}

export interface Attachment {
  partId?: string | null;
  blobId: string;
  type: string;
  name: string | null;
  size: number;
}

export interface MessageFull extends MessageSummary {
  cc?: EmailAddress[] | null;
  bcc?: EmailAddress[] | null;
  replyTo?: EmailAddress[] | null;
  messageId?: string[] | null;
  references?: string[] | null;
  bodyValues: Record<string, { value: string; isTruncated?: boolean }>;
  textBody: { partId?: string | null; type: string }[];
  htmlBody: { partId?: string | null; type: string }[];
  attachments?: Attachment[];
}

export interface ComposeAddress {
  name?: string;
  email: string;
}

export interface ComposeAttachment {
  blobId: string;
  type: string;
  name: string;
  size: number;
}

export interface SendMessageInput {
  to: ComposeAddress[];
  cc?: ComposeAddress[];
  bcc?: ComposeAddress[];
  subject: string;
  text: string;
  attachments?: ComposeAttachment[];
  // Solo al responder (Fase 4) — threading RFC 5322 §3.6.4.
  inReplyTo?: string[];
  references?: string[];
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(
  path: string,
  opts?: { redirectOn401?: boolean; method?: string; body?: unknown },
): Promise<T> {
  const res = await fetch(path, {
    credentials: "include",
    method: opts?.method,
    headers: opts?.body ? { "content-type": "application/json" } : undefined,
    body: opts?.body ? JSON.stringify(opts.body) : undefined,
  });

  if (res.status === 401) {
    // La sesión no existe o expiró — el login vive en el backend (Fase 1),
    // nunca manejamos tokens acá (CLAUDE.md §3.2). `me()` es la excepción:
    // ahí NO redirigimos, para poder mostrar la pantalla de login propia
    // (LoginPage) en vez de saltar directo a BerryKey sin que el usuario
    // vea la marca BerryLoo primero.
    if (opts?.redirectOn401 !== false) {
      window.location.href = "/auth/login";
    }
    throw new ApiError(401, "not_authenticated");
  }

  if (!res.ok) {
    const body = await res.text();
    throw new ApiError(res.status, body || res.statusText);
  }

  return res.json() as Promise<T>;
}

export interface Me {
  authenticated: true;
  sub: string;
  email?: string;
}

export const api = {
  me: () => request<Me>("/auth/me", { redirectOn401: false }),
  mailboxes: () => request<Mailbox[]>("/api/mailboxes"),
  messages: (mailboxId: string, opts?: { limit?: number; position?: number }) => {
    const params = new URLSearchParams({ mailboxId });
    if (opts?.limit) params.set("limit", String(opts.limit));
    if (opts?.position) params.set("position", String(opts.position));
    return request<MessageSummary[]>(`/api/messages?${params.toString()}`);
  },
  message: (id: string) => request<MessageFull>(`/api/messages/${encodeURIComponent(id)}`),
  sendMessage: (input: SendMessageInput) =>
    request<{ id: string }>("/api/messages/send", { method: "POST", body: input }),
  markSeen: (id: string, seen: boolean) =>
    request<{ ok: true }>(`/api/messages/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: { seen },
    }),
  archiveMessage: (id: string) =>
    request<{ ok: true }>(`/api/messages/${encodeURIComponent(id)}/archive`, { method: "POST" }),
  moveMessage: (id: string, mailboxId: string) =>
    request<{ ok: true }>(`/api/messages/${encodeURIComponent(id)}/move`, {
      method: "POST",
      body: { mailboxId },
    }),
  deleteMessage: (id: string) =>
    request<{ ok: true; permanentlyDeleted: boolean }>(`/api/messages/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),
  createMailbox: (input: { name: string; parentId?: string | null }) =>
    request<Mailbox>("/api/mailboxes", { method: "POST", body: input }),
  deleteMailbox: (id: string, force?: boolean) =>
    request<{ ok: true }>(
      `/api/mailboxes/${encodeURIComponent(id)}${force ? "?force=true" : ""}`,
      { method: "DELETE" },
    ),
  // No pasa por request(): el body es binario crudo, no JSON, así que arma
  // el fetch directo en vez de forzar ese caso especial dentro del helper
  // genérico que usa todo lo demás.
  uploadAttachment: async (file: File): Promise<ComposeAttachment> => {
    const res = await fetch("/api/attachments", {
      method: "POST",
      credentials: "include",
      headers: {
        "content-type": "application/octet-stream",
        "x-file-name": encodeURIComponent(file.name),
        "x-file-type": file.type || "application/octet-stream",
      },
      body: file,
    });
    if (res.status === 401) {
      window.location.href = "/auth/login";
      throw new ApiError(401, "not_authenticated");
    }
    if (!res.ok) {
      throw new ApiError(res.status, await res.text());
    }
    return res.json();
  },
  // El backend responde con `logoutUrl` cuando el IdP soporta RP-Initiated
  // Logout (end_session_endpoint) — el caller debe navegar ahí para cerrar
  // también la sesión del IdP, no solo revocar el token local (§3.2).
  logout: async (): Promise<{ logoutUrl?: string }> => {
    const res = await fetch("/auth/logout", { method: "POST", credentials: "include" });
    if (!res.ok) return {};
    return res.json();
  },
};
